/**
 * Construção de DOM sem framework.
 *
 * **Por que sem framework.** O design system do macOS Tahoe é HTML, CSS e um
 * `macos.js` que manipula o DOM diretamente — menus, popovers, tooltips,
 * sliders, janelas. Um DOM virtual brigaria com ele o tempo todo: o React
 * recria o nó e o comportamento do design system perde a referência, ou o
 * design system muda o nó e o React o descarta na próxima renderização.
 *
 * Então: funções que devolvem DOM de verdade, e `hidratar()` chamando
 * `MacOS.init(elemento)` depois de cada render para o design system ligar os
 * ícones e os controles do que acabou de nascer.
 */

export type Filho = Node | string | number | null | undefined | false | Filho[];

export interface Props {
  /** Estilo como objeto; `--var` também funciona. */
  style?: Record<string, string | number | null | undefined>;
  dataset?: Record<string, string | number | boolean | null | undefined>;
  /** Qualquer `on*` vira addEventListener. */
  [chave: string]: unknown;
}

const NAMESPACE_SVG = 'http://www.w3.org/2000/svg';
const TAGS_SVG = new Set(['svg', 'path', 'circle', 'rect', 'line', 'g', 'polyline', 'text']);

/**
 * `h('div.mac-card#topo', { onclick }, ['texto'])`
 *
 * A sintaxe de classe no seletor existe porque a interface inteira é feita de
 * classes do design system: escrever `class: 'mac-btn mac-btn-primary'` em toda
 * chamada esconderia o conteúdo atrás da plumbing.
 */
export function h<K extends keyof HTMLElementTagNameMap>(
  seletor: K | string,
  props?: Props | Filho,
  filhos?: Filho,
): HTMLElement {
  const { tag, classes, id } = quebrarSeletor(seletor);
  const el = TAGS_SVG.has(tag)
    ? (document.createElementNS(NAMESPACE_SVG, tag) as unknown as HTMLElement)
    : document.createElement(tag);

  if (classes.length > 0) el.setAttribute('class', classes.join(' '));
  if (id) el.id = id;

  // O segundo argumento é opcional: `h('div', 'texto')` e
  // `h('div', { class: 'x' }, 'texto')` são as duas formas válidas.
  const ehProps =
    props !== null &&
    typeof props === 'object' &&
    !Array.isArray(props) &&
    !(props instanceof Node);

  if (ehProps) {
    aplicarProps(el, props as Props);
    anexar(el, filhos);
  } else {
    anexar(el, props as Filho);
  }

  return el;
}

function quebrarSeletor(seletor: string): { tag: string; classes: string[]; id: string | null } {
  const partes = seletor.split(/(?=[.#])/);
  const tag = partes[0]?.startsWith('.') || partes[0]?.startsWith('#') ? 'div' : (partes[0] ?? 'div');
  const classes: string[] = [];
  let id: string | null = null;
  for (const p of partes) {
    if (p.startsWith('.')) classes.push(p.slice(1));
    else if (p.startsWith('#')) id = p.slice(1);
  }
  return { tag, classes, id };
}

function aplicarProps(el: HTMLElement, props: Props): void {
  for (const [chave, valor] of Object.entries(props)) {
    if (valor === null || valor === undefined || valor === false) continue;

    if (chave === 'style' && typeof valor === 'object') {
      for (const [prop, v] of Object.entries(valor as Record<string, unknown>)) {
        if (v === null || v === undefined) continue;
        if (prop.startsWith('--')) el.style.setProperty(prop, String(v));
        else el.style.setProperty(paraCss(prop), String(v));
      }
      continue;
    }

    if (chave === 'dataset' && typeof valor === 'object') {
      for (const [prop, v] of Object.entries(valor as Record<string, unknown>)) {
        if (v === null || v === undefined || v === false) continue;
        el.dataset[prop] = String(v);
      }
      continue;
    }

    if (chave.startsWith('on') && typeof valor === 'function') {
      el.addEventListener(chave.slice(2).toLowerCase(), valor as EventListener);
      continue;
    }

    if (chave === 'class' || chave === 'className') {
      const atual = el.getAttribute('class');
      el.setAttribute('class', atual ? `${atual} ${String(valor)}` : String(valor));
      continue;
    }

    if (chave === 'html') {
      el.innerHTML = String(valor);
      continue;
    }

    if (valor === true) el.setAttribute(chave, '');
    else el.setAttribute(chave, String(valor));
  }
}

function paraCss(prop: string): string {
  return prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

function anexar(pai: HTMLElement, filhos: Filho): void {
  if (filhos === null || filhos === undefined || filhos === false) return;
  if (Array.isArray(filhos)) {
    for (const f of filhos) anexar(pai, f);
    return;
  }
  if (filhos instanceof Node) {
    pai.appendChild(filhos);
    return;
  }
  pai.appendChild(document.createTextNode(String(filhos)));
}

/** Ícone do design system: `<i class="sf" data-sf="wifi">`. */
export function sf(nome: string, classe = ''): HTMLElement {
  return h(`i.sf${classe ? `.${classe.split(' ').join('.')}` : ''}`, { dataset: { sf: nome } });
}

/** Troca todo o conteúdo de um elemento e re-hidrata o design system. */
export function render(destino: HTMLElement, conteudo: Filho): void {
  destino.replaceChildren();
  anexar(destino, conteudo);
  hidratar(destino);
}

/**
 * Faz o design system reconhecer o DOM que acabou de nascer: renderiza ícones,
 * liga sliders e o resto dos comportamentos declarativos.
 */
export function hidratar(raiz: HTMLElement | Document = document): void {
  const macos = (globalThis as unknown as { MacOS?: { init?: (r: unknown) => void } }).MacOS;
  macos?.init?.(raiz);
}

/** Texto com uma parte destacada — usado no resultado da busca. */
export function destacar(texto: string, termo: string): Filho {
  if (!termo) return texto;
  const i = texto.toLowerCase().indexOf(termo.toLowerCase());
  if (i < 0) return texto;
  return [
    texto.slice(0, i),
    h('mark', { style: { background: 'var(--accent-tint-strong)', color: 'inherit', borderRadius: '3px' } }, texto.slice(i, i + termo.length)),
    texto.slice(i + termo.length),
  ];
}
